/**
 * Matches a path segment against a pattern segment and extracts the value if it's dynamic
 * @param pathSegment - The actual path segment (e.g., "123" or "room-456")
 * @param patternSegment - The pattern segment (e.g., "[id]" or "room-[roomId]")
 * @returns Object with match status and extracted value if dynamic
 */
export const matchSegment = (
  pathSegment: string,
  patternSegment: string,
): { matches: boolean; isDynamic: boolean; extractedValue?: string } => {
  // Check if pattern is a full dynamic placeholder
  if (patternSegment.startsWith("[") && patternSegment.endsWith("]")) {
    return {
      matches: true,
      isDynamic: true,
      extractedValue: pathSegment,
    };
  }

  // Check if pattern contains partial dynamic parts (e.g., "room-[roomId]")
  if (patternSegment.includes("[") && patternSegment.includes("]")) {
    // Create a regex pattern from the segment
    const pattern = patternSegment.replace(/\[([^\]]+)\]/g, "(.+)");
    const regex = new RegExp(`^${pattern}$`);
    const match = pathSegment.match(regex);

    if (match?.[1]) {
      return {
        matches: true,
        isDynamic: true,
        extractedValue: match[1],
      };
    }
    return { matches: false, isDynamic: true };
  }

  // Static segment - must match exactly
  return {
    matches: pathSegment === patternSegment,
    isDynamic: false,
  };
};

/**
 * Splits a path into segments, handling leading/trailing slashes
 * @param path - The path to split
 * @returns Array of path segments
 */
export const splitPath = (path: string): string[] =>
  path
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean);
